/**
 * Reference catalogs — TanStack Query hooks for Болезни/Вредители/Сорняки
 * + disease/pest categories (AK-232/233/234).
 *
 * Real-backend only (no mock toggle — backend endpoints exist on main and
 * are extended by this sprint's migration 047 + reference.py delta).
 * Pattern mirrors useReferenceQueries.ts (AK-231 crops pilot).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { usePagedInfinite } from '@/pages/platform-admin/reference/hooks/usePagedInfinite';
import type {
  CategoriesEnvelope,
  CategoryCreatePayload,
  DiseaseDetail,
  DiseaseListItem,
  DiseasePayload,
  OperationTypeCreatePayload,
  OperationTypeDetail,
  OperationTypeListItem,
  OperationTypeUpdatePayload,
  PagedResponse,
  PestDetail,
  PestListItem,
  PestPayload,
  ReferenceCategory,
  ReferenceListQuery,
  Unit,
  UnitCreatePayload,
  UnitUpdatePayload,
  WeedDetail,
  WeedListItem,
  WeedPayload,
} from '@/pages/platform-admin/reference/types';

export const catalogKeys = {
  diseases: (q: ReferenceListQuery) => ['reference', 'diseases', q] as const,
  disease: (id: string) => ['reference', 'diseases', 'detail', id] as const,
  pests: (q: ReferenceListQuery) => ['reference', 'pests', q] as const,
  pest: (id: string) => ['reference', 'pests', 'detail', id] as const,
  weeds: (q: ReferenceListQuery) => ['reference', 'weeds', q] as const,
  weed: (id: string) => ['reference', 'weeds', 'detail', id] as const,
  diseaseCategories: () => ['reference', 'disease-categories'] as const,
  pestCategories: () => ['reference', 'pest-categories'] as const,
  weedCategories: () => ['reference', 'weed-categories'] as const,
  weedLifeCycles: () => ['reference', 'weed-life-cycles'] as const,
  operationTypes: (category: string | undefined) =>
    ['reference', 'operation-types', category ?? 'all'] as const,
  operationType: (id: string) =>
    ['reference', 'operation-types', 'detail', id] as const,
  units: (category: string | undefined) =>
    ['reference', 'units', category ?? 'all'] as const,
};

function buildListParams(q: ReferenceListQuery): string {
  const params = new URLSearchParams();
  if (q.search) params.set('search', q.search);
  if (q.category && q.category !== 'all') params.set('category', q.category);
  if (q.life_cycle && q.life_cycle !== 'all') params.set('life_cycle', q.life_cycle);
  params.set('limit', String(q.limit ?? 200));
  params.set('offset', String(q.offset ?? 0));
  return params.toString();
}

/**
 * Бесконечные списки вредных объектов (AK-574). Фильтры те же, что у
 * одностраничных хуков; `limit/offset` подставляет `usePagedInfinite`, поэтому
 * из ключа их убираем — иначе каждая порция городила бы свой кэш.
 */
function harmfulFilterKey(q: ReferenceListQuery) {
  return {
    search: q.search ?? '',
    category: q.category ?? 'all',
    life_cycle: q.life_cycle ?? 'all',
  };
}

function buildHarmfulUrl(
  path: string,
  q: ReferenceListQuery,
  offset: number,
  limit: number,
): string {
  return `/api/v1/reference/${path}?${buildListParams({ ...q, limit, offset })}`;
}

export function useDiseasesInfinite(query: ReferenceListQuery) {
  return usePagedInfinite<DiseaseListItem>(
    ['reference', 'diseases', 'infinite', harmfulFilterKey(query)] as const,
    (offset, limit) => buildHarmfulUrl('diseases', query, offset, limit),
  );
}

export function usePestsInfinite(query: ReferenceListQuery) {
  return usePagedInfinite<PestListItem>(
    ['reference', 'pests', 'infinite', harmfulFilterKey(query)] as const,
    (offset, limit) => buildHarmfulUrl('pests', query, offset, limit),
  );
}

export function useWeedsInfinite(query: ReferenceListQuery) {
  return usePagedInfinite<WeedListItem>(
    ['reference', 'weeds', 'infinite', harmfulFilterKey(query)] as const,
    (offset, limit) => buildHarmfulUrl('weeds', query, offset, limit),
  );
}

// ---------- Diseases ----------

export function useDiseases(
  query: ReferenceListQuery,
): UseQueryResult<PagedResponse<DiseaseListItem>, Error> {
  return useQuery({
    queryKey: catalogKeys.diseases(query),
    queryFn: () =>
      api.get<PagedResponse<DiseaseListItem>>(
        `/api/v1/reference/diseases?${buildListParams(query)}`,
      ),
    staleTime: 30_000,
  });
}

/**
 * Single-disease detail fetch (AK-232 / PR #290 B1). The list endpoint returns
 * only DiseaseListItem (no pathogen_type / temps / yield-loss), so editing must
 * load the full row first — otherwise the full-replace PATCH would blank those
 * fields. Enabled only when an id is supplied (edit mode).
 */
export function useDisease(
  id: string | null,
): UseQueryResult<DiseaseDetail, Error> {
  return useQuery({
    queryKey: catalogKeys.disease(id ?? ''),
    queryFn: () =>
      api.get<DiseaseDetail>(`/api/v1/reference/diseases/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateDisease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: DiseasePayload) =>
      api.post<DiseaseDetail>('/api/v1/reference/diseases', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reference', 'diseases'] });
      qc.invalidateQueries({ queryKey: catalogKeys.diseaseCategories() });
    },
  });
}

export function useUpdateDisease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: DiseasePayload }) =>
      api.patch<DiseaseDetail>(`/api/v1/reference/diseases/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reference', 'diseases'] });
      qc.invalidateQueries({ queryKey: catalogKeys.diseaseCategories() });
    },
  });
}

export function useArchiveDisease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/v1/reference/diseases/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reference', 'diseases'] }),
  });
}

// ---------- Pests ----------

export function usePests(
  query: ReferenceListQuery,
): UseQueryResult<PagedResponse<PestListItem>, Error> {
  return useQuery({
    queryKey: catalogKeys.pests(query),
    queryFn: () =>
      api.get<PagedResponse<PestListItem>>(
        `/api/v1/reference/pests?${buildListParams(query)}`,
      ),
    staleTime: 30_000,
  });
}

/** Single-pest detail fetch (AK-233 / PR #290 B1). See useDisease rationale. */
export function usePest(id: string | null): UseQueryResult<PestDetail, Error> {
  return useQuery({
    queryKey: catalogKeys.pest(id ?? ''),
    queryFn: () => api.get<PestDetail>(`/api/v1/reference/pests/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreatePest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PestPayload) =>
      api.post<PestDetail>('/api/v1/reference/pests', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reference', 'pests'] });
      qc.invalidateQueries({ queryKey: catalogKeys.pestCategories() });
    },
  });
}

export function useUpdatePest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PestPayload }) =>
      api.patch<PestDetail>(`/api/v1/reference/pests/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reference', 'pests'] });
      qc.invalidateQueries({ queryKey: catalogKeys.pestCategories() });
    },
  });
}

export function useArchivePest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/reference/pests/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reference', 'pests'] }),
  });
}

// ---------- Weeds ----------

export function useWeeds(
  query: ReferenceListQuery,
): UseQueryResult<PagedResponse<WeedListItem>, Error> {
  return useQuery({
    queryKey: catalogKeys.weeds(query),
    queryFn: () =>
      api.get<PagedResponse<WeedListItem>>(
        `/api/v1/reference/weeds?${buildListParams(query)}`,
      ),
    staleTime: 30_000,
  });
}

/** Single-weed detail fetch (AK-234 / PR #290 B1). See useDisease rationale. */
export function useWeed(id: string | null): UseQueryResult<WeedDetail, Error> {
  return useQuery({
    queryKey: catalogKeys.weed(id ?? ''),
    queryFn: () => api.get<WeedDetail>(`/api/v1/reference/weeds/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateWeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WeedPayload) =>
      api.post<WeedDetail>('/api/v1/reference/weeds', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reference', 'weeds'] }),
  });
}

export function useUpdateWeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: WeedPayload }) =>
      api.patch<WeedDetail>(`/api/v1/reference/weeds/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reference', 'weeds'] }),
  });
}

export function useArchiveWeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/reference/weeds/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reference', 'weeds'] }),
  });
}

// ---------- Disease categories (mini-reference) ----------

export function useDiseaseCategories(): UseQueryResult<CategoriesEnvelope, Error> {
  return useQuery({
    queryKey: catalogKeys.diseaseCategories(),
    queryFn: () =>
      api.get<CategoriesEnvelope>('/api/v1/reference/disease-categories'),
    staleTime: 60_000,
  });
}

export function useCreateDiseaseCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CategoryCreatePayload) =>
      api.post<ReferenceCategory>('/api/v1/reference/disease-categories', p),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogKeys.diseaseCategories() }),
  });
}

export function useUpdateDiseaseCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CategoryCreatePayload }) =>
      api.patch<ReferenceCategory>(
        `/api/v1/reference/disease-categories/${id}`,
        payload,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogKeys.diseaseCategories() }),
  });
}

export function useDeleteDiseaseCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/v1/reference/disease-categories/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogKeys.diseaseCategories() }),
  });
}

// ---------- Pest categories (mini-reference) ----------

export function usePestCategories(): UseQueryResult<CategoriesEnvelope, Error> {
  return useQuery({
    queryKey: catalogKeys.pestCategories(),
    queryFn: () =>
      api.get<CategoriesEnvelope>('/api/v1/reference/pest-categories'),
    staleTime: 60_000,
  });
}

export function useCreatePestCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CategoryCreatePayload) =>
      api.post<ReferenceCategory>('/api/v1/reference/pest-categories', p),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogKeys.pestCategories() }),
  });
}

export function useUpdatePestCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CategoryCreatePayload }) =>
      api.patch<ReferenceCategory>(
        `/api/v1/reference/pest-categories/${id}`,
        payload,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogKeys.pestCategories() }),
  });
}

export function useDeletePestCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/v1/reference/pest-categories/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogKeys.pestCategories() }),
  });
}

// ---------- Weed categories / life cycles (mini-reference, migration 106) ----
//
// Сорняки — единственный вредный объект, у которого ДВА мини-справочника:
// категория (двудольные/однодольные/осоковые) и жизненный цикл
// (однолетние/двулетние/многолетние). Контракт эндпоинтов 1:1 с
// disease-/pest-categories, поэтому переиспользуем те же типы.

export function useWeedCategories(): UseQueryResult<CategoriesEnvelope, Error> {
  return useQuery({
    queryKey: catalogKeys.weedCategories(),
    queryFn: () =>
      api.get<CategoriesEnvelope>('/api/v1/reference/weed-categories'),
    staleTime: 60_000,
  });
}

export function useCreateWeedCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CategoryCreatePayload) =>
      api.post<ReferenceCategory>('/api/v1/reference/weed-categories', p),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogKeys.weedCategories() }),
  });
}

export function useUpdateWeedCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CategoryCreatePayload }) =>
      api.patch<ReferenceCategory>(
        `/api/v1/reference/weed-categories/${id}`,
        payload,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogKeys.weedCategories() }),
  });
}

export function useDeleteWeedCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/v1/reference/weed-categories/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogKeys.weedCategories() }),
  });
}

export function useWeedLifeCycles(): UseQueryResult<CategoriesEnvelope, Error> {
  return useQuery({
    queryKey: catalogKeys.weedLifeCycles(),
    queryFn: () =>
      api.get<CategoriesEnvelope>('/api/v1/reference/weed-life-cycles'),
    staleTime: 60_000,
  });
}

export function useCreateWeedLifeCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CategoryCreatePayload) =>
      api.post<ReferenceCategory>('/api/v1/reference/weed-life-cycles', p),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogKeys.weedLifeCycles() }),
  });
}

export function useUpdateWeedLifeCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CategoryCreatePayload }) =>
      api.patch<ReferenceCategory>(
        `/api/v1/reference/weed-life-cycles/${id}`,
        payload,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogKeys.weedLifeCycles() }),
  });
}

export function useDeleteWeedLifeCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/v1/reference/weed-life-cycles/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogKeys.weedLifeCycles() }),
  });
}

// ---------- Operation types (AK-238 — тех. операции) ----------

/**
 * Список тех. операций. В отличие от diseases/pests/weeds этот endpoint
 * отдаёт ПЛОСКИЙ массив (не PagedResponse) и не поддерживает `search` —
 * текстовый поиск делаем клиентски. Фильтр по `category` — серверный.
 */
export function useOperationTypes(
  category?: string,
): UseQueryResult<OperationTypeListItem[], Error> {
  return useQuery({
    queryKey: catalogKeys.operationTypes(category),
    queryFn: () => {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.set('category', category);
      const qs = params.toString();
      return api.get<OperationTypeListItem[]>(
        `/api/v1/reference/operation-types${qs ? `?${qs}` : ''}`,
      );
    },
    staleTime: 30_000,
  });
}

/**
 * Single operation-type detail (AK-238). Edit-форма строится из detail-fetch
 * (PR #290 B1 паттерн) — даже несмотря на то, что list уже отдаёт полную
 * карточку: detail гарантирует свежесть и единый паттерн со всеми drawer'ами.
 */
export function useOperationType(
  id: string | null,
): UseQueryResult<OperationTypeDetail, Error> {
  return useQuery({
    queryKey: catalogKeys.operationType(id ?? ''),
    queryFn: () =>
      api.get<OperationTypeDetail>(`/api/v1/reference/operation-types/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateOperationType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: OperationTypeCreatePayload) =>
      api.post<OperationTypeDetail>('/api/v1/reference/operation-types', payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['reference', 'operation-types'] }),
  });
}

export function useUpdateOperationType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: OperationTypeUpdatePayload;
    }) =>
      api.patch<OperationTypeDetail>(
        `/api/v1/reference/operation-types/${id}`,
        payload,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['reference', 'operation-types'] }),
  });
}

export function useArchiveOperationType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/v1/reference/operation-types/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['reference', 'operation-types'] }),
  });
}

// ---------- Reference merge (diseases / pests / weeds) ----------

export interface ReferenceMergeResult {
  source_id: string;
  target_id: string;
  entity_type: string;
  dry_run: boolean;
  repointed: Record<string, number>;
  deleted_conflicts: Record<string, number>;
  total_repointed: number;
  total_deleted: number;
  archived: boolean;
  no_op: boolean;
  message: string;
}

export function useMergeDiseases() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, targetId, dryRun = false }: { sourceId: string; targetId: string; dryRun?: boolean }) =>
      api.post<ReferenceMergeResult>(
        `/api/v1/reference/diseases/${sourceId}/merge-into/${targetId}`,
        { dry_run: dryRun },
      ),
    onSuccess: (_data, variables) => {
      if (variables.dryRun) return;
      qc.invalidateQueries({ queryKey: ['reference', 'diseases'] });
    },
  });
}

export function useMergePests() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, targetId, dryRun = false }: { sourceId: string; targetId: string; dryRun?: boolean }) =>
      api.post<ReferenceMergeResult>(
        `/api/v1/reference/pests/${sourceId}/merge-into/${targetId}`,
        { dry_run: dryRun },
      ),
    onSuccess: (_data, variables) => {
      if (variables.dryRun) return;
      qc.invalidateQueries({ queryKey: ['reference', 'pests'] });
    },
  });
}

export function useMergeWeeds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, targetId, dryRun = false }: { sourceId: string; targetId: string; dryRun?: boolean }) =>
      api.post<ReferenceMergeResult>(
        `/api/v1/reference/weeds/${sourceId}/merge-into/${targetId}`,
        { dry_run: dryRun },
      ),
    onSuccess: (_data, variables) => {
      if (variables.dryRun) return;
      qc.invalidateQueries({ queryKey: ['reference', 'weeds'] });
    },
  });
}

// ---------- Units (reference.units — PK = code) ----------

export function useUnits(
  category?: string,
): UseQueryResult<Unit[], Error> {
  return useQuery({
    queryKey: catalogKeys.units(category),
    queryFn: () => {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.set('category', category);
      const qs = params.toString();
      return api.get<Unit[]>(
        `/api/v1/reference/units${qs ? `?${qs}` : ''}`,
      );
    },
    staleTime: 30_000,
  });
}

export function useCreateUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UnitCreatePayload) =>
      api.post<Unit>('/api/v1/reference/units', payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['reference', 'units'] }),
  });
}

export function useUpdateUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, payload }: { code: string; payload: UnitUpdatePayload }) =>
      api.patch<Unit>(`/api/v1/reference/units/${code}`, payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['reference', 'units'] }),
  });
}

export function useArchiveUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api.delete<void>(`/api/v1/reference/units/${code}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['reference', 'units'] }),
  });
}

// ---------- Полевые справочники: состояния поля / типы угодий ----------
//
// Пользовательские экраны читают эти каталоги через `lib/reference/fieldCatalogs`
// (только активные, кэш 5 минут). Админке нужен другой срез: архивные строки
// тоже видны, плюс счётчик использования — чтобы не убрать в архив то, на чём
// висят посевы и поля. Поэтому отдельные ключи, а не переиспользование тех.

export type FieldCatalogKind = 'field-states' | 'land-categories';

export interface FieldCatalogAdminRow {
  id: string;
  code: string;
  name_ru: string;
  name_translations: Record<string, string>;
  color: string | null;
  sort_order: number;
  is_active: boolean;
  usage_count: number;
  is_system: boolean;
  runs_crop_rotation?: boolean;
}

export interface FieldCatalogAdminEnvelope {
  items: FieldCatalogAdminRow[];
}

export interface FieldCatalogCreateBody {
  code: string;
  name_translations: Record<string, string>;
  color: string | null;
  sort_order: number;
  runs_crop_rotation?: boolean;
}

export type FieldCatalogUpdateBody = Omit<FieldCatalogCreateBody, 'code'>;

export const fieldCatalogAdminKeys = {
  list: (kind: FieldCatalogKind) => ['reference', kind, 'admin'] as const,
};

export function useFieldCatalogAdminList(
  kind: FieldCatalogKind,
): UseQueryResult<FieldCatalogAdminEnvelope, Error> {
  return useQuery({
    queryKey: fieldCatalogAdminKeys.list(kind),
    queryFn: () =>
      api.get<FieldCatalogAdminEnvelope>(
        `/api/v1/reference/${kind}?active_only=false&with_usage=true`,
      ),
    staleTime: 30_000,
  });
}

/**
 * После любой правки чистим ОБА кэша: админский срез и пользовательский
 * (`['reference', kind]` — префикс общий), иначе форма посева ещё пять минут
 * предлагала бы старый список.
 */
function useFieldCatalogInvalidate(kind: FieldCatalogKind) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['reference', kind] });
}

export function useCreateFieldCatalogItem(kind: FieldCatalogKind) {
  const invalidate = useFieldCatalogInvalidate(kind);
  return useMutation({
    mutationFn: (payload: FieldCatalogCreateBody) =>
      api.post<FieldCatalogAdminRow>(`/api/v1/reference/${kind}`, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateFieldCatalogItem(kind: FieldCatalogKind) {
  const invalidate = useFieldCatalogInvalidate(kind);
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: FieldCatalogUpdateBody }) =>
      api.patch<FieldCatalogAdminRow>(`/api/v1/reference/${kind}/${id}`, payload),
    onSuccess: invalidate,
  });
}

/** Архивация вместо удаления: справочник глобальный, на строку ссылаются данные. */
export function useArchiveFieldCatalogItem(kind: FieldCatalogKind) {
  const invalidate = useFieldCatalogInvalidate(kind);
  return useMutation({
    mutationFn: (id: string) =>
      api.post<FieldCatalogAdminRow>(`/api/v1/reference/${kind}/${id}/archive`, {}),
    onSuccess: invalidate,
  });
}

export function useRestoreFieldCatalogItem(kind: FieldCatalogKind) {
  const invalidate = useFieldCatalogInvalidate(kind);
  return useMutation({
    mutationFn: (id: string) =>
      api.post<FieldCatalogAdminRow>(`/api/v1/reference/${kind}/${id}/restore`, {}),
    onSuccess: invalidate,
  });
}
